import React, { useState } from 'react';
import {
  AutoComplete,
  Card,
  Input,
  InputNumber,
  Button,
  Space,
  Typography,
  message,
  Divider,
  Radio,
  Tag,
  Spin,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined, SearchOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  DUBBO_QUERY_PROVIDERS,
  DUBBO_GENERIC_INVOKE,
  DubboProviderInfo,
} from './graphql/dubboTools';

interface Param {
  type: string;
  value: string;
}

const DEFAULT_PORT = 20880;

// 常用 Java 参数类型（含 String 及基础包装类型），供“类型”输入框自动补全
const JAVA_TYPE_OPTIONS = [
  'java.lang.String',
  'java.lang.Long',
  'java.lang.Integer',
  'java.lang.Boolean',
  'java.lang.Double',
  'java.lang.Float',
  'java.lang.Short',
  'java.lang.Byte',
  'java.lang.Character',
  'java.lang.Object',
  'java.math.BigDecimal',
  'java.util.List',
  'java.util.Map',
  'java.util.Set',
].map((v) => ({ value: v }));

const DubboToolsPage: React.FC = () => {
  const [interfaceName, setInterfaceName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState<number>(DEFAULT_PORT);
  const [version, setVersion] = useState('');
  const [group, setGroup] = useState('');
  const [selectedProviderKey, setSelectedProviderKey] = useState<string | undefined>(undefined);
  const [methodName, setMethodName] = useState('');
  const [params, setParams] = useState<Param[]>([{ type: '', value: '' }]);
  const [result, setResult] = useState<string | null>(null);

  const [queryProviders, { data: providersData, loading: queryLoading }] = useLazyQuery<{
    dubboQueryProviders: DubboProviderInfo[];
  }>(DUBBO_QUERY_PROVIDERS, { fetchPolicy: 'no-cache' });

  const [invoke, { loading: invokeLoading }] = useMutation(DUBBO_GENERIC_INVOKE);

  const providers: DubboProviderInfo[] = providersData?.dubboQueryProviders ?? [];

  const handleSearch = () => {
    if (!interfaceName.trim()) { message.warning('请输入接口名'); return; }
    setSelectedProviderKey(undefined);
    setResult(null);
    queryProviders({ variables: { interfaceName: interfaceName.trim() } });
  };

  const handleSelectProvider = (p: DubboProviderInfo) => {
    setSelectedProviderKey(`${p.host}:${p.port}:${p.version}:${p.group}`);
    setHost(p.host);
    setPort(p.port);
    setVersion(p.version);
    setGroup(p.group);
  };

  const addParam = () => setParams((prev) => [...prev, { type: '', value: '' }]);
  const removeParam = (index: number) => setParams((prev) => prev.filter((_, i) => i !== index));
  const updateParam = (index: number, field: keyof Param, value: string) =>
    setParams((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));

  // 用浏览器内置 JSON 美化，不引入额外依赖；非法 JSON 时提示不动
  const formatParamJson = (index: number) => {
    const raw = params[index]?.value?.trim();
    if (!raw) return;
    try {
      updateParam(index, 'value', JSON.stringify(JSON.parse(raw), null, 2));
    } catch {
      message.error('JSON 格式不正确，无法格式化');
    }
  };

  const handleInvoke = async () => {
    if (!host.trim()) { message.warning('请输入 Host'); return; }
    if (!methodName.trim()) { message.warning('请输入方法名'); return; }

    const noParams = params.length === 1 && !params[0].type && !params[0].value;
    const hasEmptyParam = params.some((p) => !p.type.trim() || !p.value.trim());
    if (!noParams && hasEmptyParam) {
      message.warning('参数类型和参数值不能为空，如无参数请删除参数行');
      return;
    }

    const paramTypes = noParams ? [] : params.map((p) => p.type.trim());
    const argsJson = noParams ? [] : params.map((p) => p.value.trim());

    try {
      const { data } = await invoke({
        variables: {
          host: host.trim(), port,
          interfaceName: interfaceName.trim(),
          version: version.trim(),
          group: group.trim(),
          methodName: methodName.trim(),
          paramTypes, argsJson,
        },
      });
      const raw = data?.dubboGenericInvoke ?? '';
      try { setResult(JSON.stringify(JSON.parse(raw), null, 2)); }
      catch { setResult(raw); }
    } catch (e: any) {
      const errMsg = e?.graphQLErrors?.[0]?.message ?? e?.message ?? '调用失败';
      setResult(`ERROR: ${errMsg}`);
      message.error('调用失败，见结果区');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Typography.Title level={4}>Dubbo 远程调试工具</Typography.Title>

      <Card title="① 接口 & Provider" size="small">
        <Space direction="vertical" className="w-full">
          <Space.Compact style={{ width: '100%' }}>
            <Input
              value={interfaceName}
              onChange={(e) => setInterfaceName(e.target.value)}
              placeholder="ltd.tita.apiserver.api.dubbo.ChannelDubboService"
              onPressEnter={handleSearch}
            />
            <Button type="primary" icon={<SearchOutlined />} loading={queryLoading} onClick={handleSearch}>
              从 Nacos 搜索
            </Button>
          </Space.Compact>

          {queryLoading && <Spin />}

          {!queryLoading && providers.length > 0 && (
            <div>
              <div className="text-gray-500 text-xs mb-2">共 {providers.length} 个健康实例，点击自动填入连接信息：</div>
              <Radio.Group
                value={selectedProviderKey}
                onChange={(e) => {
                  const key = e.target.value as string;
                  const p = providers.find((p) => `${p.host}:${p.port}:${p.version}:${p.group}` === key);
                  if (p) handleSelectProvider(p);
                }}
              >
                <Space direction="vertical">
                  {providers.map((p) => {
                    const key = `${p.host}:${p.port}:${p.version}:${p.group}`;
                    return (
                      <Radio key={key} value={key}>
                        <Space>
                          <Tag color="blue">{p.host}:{p.port}</Tag>
                          {p.version && <Tag color="green">v{p.version}</Tag>}
                          {p.group && <Tag color="orange">{p.group}</Tag>}
                        </Space>
                      </Radio>
                    );
                  })}
                </Space>
              </Radio.Group>
            </div>
          )}

          {!queryLoading && providersData && providers.length === 0 && (
            <div className="text-gray-400 text-sm">未找到健康的 Provider 实例</div>
          )}

          <Divider plain style={{ margin: '8px 0' }}>连接信息（可手动修改）</Divider>
          <Space wrap>
            <Input addonBefore="Host" value={host} onChange={(e) => setHost(e.target.value)} placeholder="192.168.0.45" style={{ width: 280 }} />
            <InputNumber addonBefore="Port" value={port} min={1} max={65535} onChange={(v) => setPort(Number(v) || DEFAULT_PORT)} style={{ width: 160 }} />
            <Input addonBefore="Version" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.0.0" style={{ width: 180 }} />
            <Input addonBefore="Group" value={group} onChange={(e) => setGroup(e.target.value)} placeholder="api-server" style={{ width: 220 }} />
          </Space>
        </Space>
      </Card>

      <Card title="② 调用接口" size="small">
        <Space direction="vertical" className="w-full">
          <Input
            addonBefore="方法名"
            value={methodName}
            onChange={(e) => setMethodName(e.target.value)}
            placeholder="getChannelById"
          />

          <Divider plain style={{ margin: '8px 0' }}>参数列表（无参数时删除所有行或留空）</Divider>

          {params.map((p, i) => (
            <div key={i} style={{ width: '100%' }}>
              <Typography.Text strong style={{ fontSize: 15 }}>参数{i + 1}</Typography.Text>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', width: '100%', marginTop: 4 }}>
                <MinusCircleOutlined className="text-red-400 cursor-pointer mt-2" style={{ flex: '0 0 auto' }} onClick={() => removeParam(i)} />
                <AutoComplete
                  value={p.type}
                  options={JAVA_TYPE_OPTIONS}
                  onChange={(v) => updateParam(i, 'type', v)}
                  filterOption={(input, option) =>
                    (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: 280, flex: '0 0 280px' }}
                >
                  <Input addonBefore="类型" placeholder="java.lang.String" />
                </AutoComplete>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Input.TextArea
                    value={p.value}
                    onChange={(e) => updateParam(i, 'value', e.target.value)}
                    placeholder='JSON值：字符串直接输入 test，数字输入 1，JSON 输入 {"k":"v"}（可拖动下边缘调高）'
                    rows={4}
                    style={{ width: '100%', fontFamily: 'monospace', resize: 'vertical' }}
                  />
                  <Button size="small" style={{ alignSelf: 'flex-start' }} onClick={() => formatParamJson(i)}>格式化 JSON</Button>
                </div>
              </div>
            </div>
          ))}

          <Button type="dashed" icon={<PlusOutlined />} onClick={addParam} style={{ width: 180 }}>添加参数</Button>

          <Button
            type="primary"
            loading={invokeLoading}
            disabled={!host.trim() || !interfaceName.trim() || !methodName.trim()}
            onClick={handleInvoke}
          >
            调用
          </Button>
        </Space>
      </Card>

      {result !== null && (
        <Card title="调用结果" size="small">
          <Typography.Paragraph
            copyable
            style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12, marginBottom: 0, maxHeight: 500, overflowY: 'auto' }}
          >
            {result}
          </Typography.Paragraph>
        </Card>
      )}
    </div>
  );
};

export default DubboToolsPage;
